"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OperationList = void 0;
const tslib_1 = require("tslib");
const css_1 = require("@emotion/css");
const react_1 = tslib_1.__importStar(require("react"));
const dnd_1 = require("@hello-pangea/dnd");
const react_use_1 = require("react-use");
const ui_1 = require("@grafana/ui");
const OperationEditor_1 = require("./OperationEditor");
const QueryEditor_1 = require("../../QueryEditor");
function OperationList({ query, datasource, queryModeller, onChange, onRunQuery, highlightedOp, timeRange, isConflictingOperation, }) {
    const styles = (0, ui_1.useStyles2)(getStyles);
    const { operations } = query;
    const opsToHighlight = useOperationsHighlight(operations);
    const [cascaderOpen, setCascaderOpen] = (0, react_1.useState)(false);
    const onOperationChange = (index, update) => {
        const updatedList = [...operations];
        updatedList.splice(index, 1, update);
        onChange(Object.assign(Object.assign({}, query), { operations: updatedList }));
    };
    const onRemove = (index) => {
        const updatedList = [...operations.slice(0, index), ...operations.slice(index + 1)];
        onChange(Object.assign(Object.assign({}, query), { operations: updatedList }));
    };
    const onToggle = (index) => {
        onOperationChange(index, Object.assign(Object.assign({}, operations[index]), { disabled: !operations[index].disabled }));
    };
    const addOptions = queryModeller.getCategories().map((category) => {
        return {
            value: category,
            label: category,
            items: queryModeller.getOperationsForCategory(category).map((operation) => ({
                value: operation.id,
                label: operation.name,
                isLeaf: true,
            })),
        };
    });
    const onAddOperation = (value) => {
        const operationDef = queryModeller.getOperationDefinition(value);
        if (!operationDef) {
            return;
        }
        onChange(operationDef.addOperationHandler(operationDef, query, queryModeller));
        setCascaderOpen(false);
    };
    const onDragEnd = (result) => {
        if (!result.destination) {
            return;
        }
        const updatedList = [...operations];
        const element = updatedList[result.source.index];
        updatedList.splice(result.source.index, 1);
        updatedList.splice(result.destination.index, 0, element);
        onChange(Object.assign(Object.assign({}, query), { operations: updatedList }));
    };
    const onCascaderBlur = () => {
        setCascaderOpen(false);
    };
    return (react_1.default.createElement(QueryEditor_1.EditorStack, { gap: 1, direction: "column" },
        react_1.default.createElement(QueryEditor_1.EditorStack, { gap: 1 },
            operations.length > 0 && (react_1.default.createElement(dnd_1.DragDropContext, { onDragEnd: onDragEnd },
                react_1.default.createElement(dnd_1.Droppable, { droppableId: "sortable-field-mappings", direction: "horizontal" }, (provided) => (react_1.default.createElement("div", Object.assign({ className: styles.operationList, ref: provided.innerRef }, provided.droppableProps),
                    operations.map((op, index) => {
                        return (react_1.default.createElement(OperationEditor_1.OperationEditor, { key: op.id + JSON.stringify(op.params) + index, queryModeller: queryModeller, index: index, operation: op, query: query, datasource: datasource, onChange: onOperationChange, onRemove: onRemove, onToggle: onToggle, onRunQuery: onRunQuery, flash: opsToHighlight[index], highlight: highlightedOp === op, timeRange: timeRange, isConflictingOperation: isConflictingOperation }));
                    }),
                    provided.placeholder))))),
            react_1.default.createElement("div", { className: styles.addButton }, cascaderOpen ? (react_1.default.createElement(ui_1.Cascader, { options: addOptions, onSelect: onAddOperation, onBlur: onCascaderBlur, autoFocus: true, alwaysOpen: true, hideActiveLevelLabel: true, placeholder: 'Search' })) : (react_1.default.createElement(ui_1.Button, { icon: 'plus', variant: 'secondary', onClick: () => setCascaderOpen(true), title: 'Add operation' }, "Operations"))))));
}
exports.OperationList = OperationList;
/**
 * Returns indexes of operations that should be highlighted. We check the diff of operations added but at the same time
 * we want to highlight operations only after the initial render, so we check for mounted state and calculate the diff
 * only after.
 * @param operations
 */
function useOperationsHighlight(operations) {
    const isMounted = (0, react_use_1.useMountedState)();
    const prevOperations = (0, react_use_1.usePrevious)(operations);
    if (!isMounted()) {
        return operations.map(() => false);
    }
    if (!prevOperations) {
        return operations.map(() => true);
    }
    let newOps = [];
    if (prevOperations.length - 1 === operations.length && operations.every((op) => prevOperations.includes(op))) {
        // In case we remove one op and does not change any ops then don't highlight anything.
        return operations.map(() => false);
    }
    if (prevOperations.length + 1 === operations.length && prevOperations.every((op) => operations.includes(op))) {
        // If we add a single op just find it and highlight just that.
        const newOp = operations.find((op) => !prevOperations.includes(op));
        newOps = operations.map((op) => {
            return op === newOp;
        });
    }
    else {
        // Default diff of all ops.
        newOps = operations.map((op, index) => {
            var _a;
            return !isSameOp(op.id, (_a = prevOperations[index]) === null || _a === void 0 ? void 0 : _a.id);
        });
    }
    return newOps;
}
function isSameOp(op1, op2) {
    return op1 === op2 || `__${op1}_by` === op2 || op1 === `__${op2}_by`;
}
const getStyles = (theme) => {
    return {
        heading: (0, css_1.css)({
            label: 'heading',
            fontSize: 12,
            fontWeight: theme.typography.fontWeightMedium,
            marginBottom: 0,
        }),
        operationList: (0, css_1.css)({
            label: 'operationList',
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(2),
        }),
        addButton: (0, css_1.css)({
            label: 'addButton',
            width: 126,
            paddingBottom: theme.spacing(1),
        }),
    };
};
//# sourceMappingURL=OperationList.js.map